/**
 * Handles the search page entirely
 * Set filters using mutations anywhere in the UI and it will trigger search
 *
 * ! The initial filter values are declared here
 */
import isEmpty from 'lodash/isEmpty';
import MutationTypes from '~/config/MutationTypes';
import FeatureFlagNames from '~/config/Constants/FeatureFlags';
import featureFlags from '~/services/FeatureFlagsService.js';
import SessionStorage from '~/services/SessionStorage';
import { DISCOVERY_FILTER_FILTERS_KEY, DISCOVERY_FILTER_TYPE_KEY } from '~/discovery/utils/getPayload';

export function state() {
  return {
    term: '',
    filters: {
      contentTypes: featureFlags().isEnabled(FeatureFlagNames.VIDEO_AND_RC_ACTIVITY)
        ? ['quiz', 'presentation', 'video-quiz', 'reading-quiz']
        : ['quiz', 'presentation'],
    },
    sortBy: { key: '_score', order: 'desc' },
    quizIdsTagged: {},
    source: null,
    mathStandards: {
      state: false,
      grade: false,
      selectedTerm: false,
    },
    discovery: {
      type: 'topics',
      filters: [],
    },
    showPremiumResultsOnly: false,
    isDiscoveryExpEnabled: true,
  };
}

export const getters = {
  searchTerm(state) {
    return state.term;
  },

  activeFilters(state) {
    return state.filters;
  },

  activeSortKey(state) {
    return state.sortBy;
  },

  taggedQuizIds(state) {
    return state.quizIdsTagged;
  },

  searchSource(state) {
    return state.source;
  },

  mathStandards(state) {
    return state.mathStandards;
  },

  discovery(state) {
    return state.discovery;
  },

};

export const actions = {
  setSearchTerm({ commit }, term) {
    commit(MutationTypes.searchState.SET_SEARCH_TERM, term);
  },

  setFilterTerm({ commit }, { term, value }) {
    commit(MutationTypes.searchState.SET_FILTER_TERM, { term, value });
  },

  setSortKey({ commit }, sortBy) {
    commit(MutationTypes.searchState.SET_SORT_KEY, sortBy);
  },

  setAllFilters({ commit }, filters) {
    commit(MutationTypes.searchState.SET_ALL_FILTERS, filters);
  },

  resetFilters({ commit }, filters) {
    commit(MutationTypes.searchState.RESET_FILTERS, filters);
  },

  tagQuizId({ commit }, { quizId, isTagged }) {
    commit(MutationTypes.searchState.TAG_QUIZ_ID, { quizId, isTagged });
  },

  setSearchSource({ commit }, source) {
    commit(MutationTypes.searchState.SET_SEARCH_SOURCE, source);
  },

  setMathStandards({ commit }, mathStandards) {
    commit(MutationTypes.searchState.SET_MATH_STANDARDS, mathStandards);
  },

  toggleShowPremiumResultsOnly({ commit }, shouldShow) {
    commit(MutationTypes.searchState.TOGGLE_SHOW_PREMIUM_RESULTS_ONLY, shouldShow);
  },
  setShowPremiumResultsOnly({ commit }, shouldShow) {
    commit(MutationTypes.searchState.SET_SHOW_PREMIUM_RESULTS_ONLY, shouldShow);
  },
  updateDiscoveryExpEnabled({ commit }, isDiscoveryExpEnabled) {
    commit(MutationTypes.searchState.UPDATE_DISCOVERY_EXP_ENABLED, isDiscoveryExpEnabled);
  },

  setDiscoveryFilterFilters({ commit }, discoveryFilters) {
    commit(MutationTypes.searchState.SET_DISCOVERY_FILTER_FILTERS, discoveryFilters);
  },

  setDiscoveryFilterType({ commit }, type = 'topics') {
    commit(MutationTypes.searchState.SET_DISCOVERY_FILTER_TYPE, type);
  },
};

export const mutations = {
  [MutationTypes.searchState.SET_SEARCH_TERM](state, term) {
    state.term = term;
  },

  [MutationTypes.searchState.SET_FILTER_TERM](state, { term, value }) {
    if (isEmpty(value)) {
      const filters = { ...state.filters };
      delete filters[term];
      state.filters = filters;
      return;
    }
    state.filters[term] = value;
  },

  [MutationTypes.searchState.SET_ALL_FILTERS](state, filters) {
    state.filters = { ...state.filters, ...filters };
  },

  [MutationTypes.searchState.RESET_FILTERS](state, filters) {
    state.filters = filters;
    state.discovery.filters = [];
    SessionStorage.setItem(DISCOVERY_FILTER_FILTERS_KEY, []);
  },

  [MutationTypes.searchState.SET_SORT_KEY](state, sortBy) {
    state.sortBy = sortBy;
  },

  [MutationTypes.searchState.TAG_QUIZ_ID](state, { quizId, isTagged = false }) {
    const incrementFactor = isTagged ? 1 : -1;
    state.quizIdsTagged[quizId] = (state.quizIdsTagged[quizId] ?? 0) + incrementFactor;
  },

  [MutationTypes.searchState.SET_SEARCH_SOURCE](state, source) {
    state.source = source;
  },

  [MutationTypes.searchState.SET_MATH_STANDARDS](state, mathStandards) {
    state.mathStandards = { ...state.mathStandards, ...mathStandards };
  },
  [MutationTypes.searchState.TOGGLE_SHOW_PREMIUM_RESULTS_ONLY](state, shouldShow) {
    state.showPremiumResultsOnly = shouldShow;
  },
  [MutationTypes.searchState.SET_SHOW_PREMIUM_RESULTS_ONLY](state, shouldShow) {
    state.showPremiumResultsOnly = shouldShow;
  },
  [MutationTypes.searchState.UPDATE_DISCOVERY_EXP_ENABLED](state, isDiscoveryExpEnabled) {
    state.isDiscoveryExpEnabled = isDiscoveryExpEnabled;
  },
  [MutationTypes.searchState.SET_DISCOVERY_FILTER_FILTERS](state, discoveryFilters) {
    state.discovery.filters = discoveryFilters;
    SessionStorage.setItem(DISCOVERY_FILTER_FILTERS_KEY, discoveryFilters);
  },
  [MutationTypes.searchState.SET_DISCOVERY_FILTER_TYPE](state, type) {
    state.discovery.type = type;
    SessionStorage.setItem(DISCOVERY_FILTER_TYPE_KEY, type);
  },
};

const searchState = {
  state,
  getters,
  mutations,
  actions,
};

export default searchState;
